// mixins
import { userAccess } from '@/mixins/user-access'
import { displayAlert } from '@/mixins/alert'
import { utils } from '@/mixins/utils'

export const promoItemsProperties = {
  data () {
    return {
      formatFields: [
        'cost', 'retail_price', 'promotion_price', 'amount_off',
        'rebate_amount', 'billback_allowance', 'amap_allowance', 'early_buy_allowance', 'variance', 'accrual_allowance', 'complex_promo_price'
      ]
    }
  },
  mixins: [userAccess, displayAlert, utils],
  computed: {
    scanAllowanceFields() {
      return ['rebate_amount', 'ad_scan', 'edlc_scan', 'tpr_scan']
    },
    billbackAllowanceFields() {
      let fields = ['billback_allowance', 'ad_billback', 'edlc_billback', 'tpr_billback']
      if (this.$auth.tenant === 'awg') {
        fields = [...fields, 'amap_allowance']
      }
      return fields
    },
    isComplexPromo () {
      return ['BUY_X_GET_X', 'BUY_X_SAVE_$Y'].includes(this.promo.promotion_type.constant)
    },
    isScanPromo () {
      // AMOUNT_OFF, BUY_X_SAVE_$Y, BUY_X_GET_X
      return this.promo?.promo_category?.scan_promotion
        && !this.isComplexPromo
    },
    usePromoItemQtyPerCase () {
      return this.$auth.tenant === 'pricechopper' && this.promo?.promo_category?.name.toLowerCase().includes('dsd')
    },
    maxBuyItemGroupRedemptionAllowance () {
      return Math.max(...this.itemGroupList.flatMap(ig => Number(ig.rebate_amount)))
    },
    isDSDOffInvoice () {
      return (this.$auth.tenant === 'alliance-retail-group') && (this.promo?.promo_category?.name === 'DSD Off Invoice')
    },
    costStackConfig () {
      return {
        EDLC: ['TPR', 'EDLC', 'AMAP', 'EBA'],
        EBA: [],
        AMAP: [],
        PCAF: [],
        TPR: ['TPR', 'EDLC', 'AMAP', 'EBA'],
        AD: this.isDSDOffInvoice ? [] : ['AD', 'TPR', 'EDLC', 'AMAP', 'EBA']
      }
    },
    netUnitAllowanceCostStackConfig () {
      return {
        EDLC: ['TPR', 'EDLC', 'AMAP', 'EBA'],
        EBA: [],
        AMAP: [],
        PCAF: [],
        TPR: ['TPR', 'EDLC', 'AMAP', 'EBA'],
        AD: this.isDSDOffInvoice ? [] : ['AD', 'AMAP', 'EBA']
      }
    }
  },
  methods: {
    formatPromoItem (promoItem) {
      promoItem.vendor_user_item = this.checkVendorItemAccess && this.relatedVendorIds.includes(promoItem.item?.vendor_id)
      for (const field of this.formatFields) {
        promoItem[field] = this.getFormattedDisplayValue(promoItem, field)
      }
      promoItem.item = this.formatItemPrices(promoItem.item)
      if (this.isAccrualPromo) {
        if (promoItem.accrual_amt != null) {
          promoItem.billback_allowance = this.getFormattedPrice(promoItem.accrual_amt)
        }
      }
      return this.handleItemCalc(promoItem)
    },
    handleItemCalc (promoItem) {
      if (this.$auth.tenant === 'awg' && !this.isScanPromo && !this.isAMAP && Array.isArray(promoItem.item.amap_allowances)) {
        const filteredAMAPs = this.getFilteredAMAPs(promoItem.item)
        if (filteredAMAPs) {
          const totalAMAPs = this.calcTotalAMAPs(filteredAMAPs)
          promoItem.amap_allowance_total = `$${this.$config.formatCurrency(totalAMAPs)}`
        }
        promoItem.item.amap_allowances = filteredAMAPs
      }
      const validAllowances = this.getValidActiveAllowances(promoItem, this.costStackConfig)
      promoItem.has_active_allowances = (validAllowances.length > 0)
      if (promoItem.has_active_allowances) {
        promoItem.item.filtered_allowances = validAllowances
        if (this.$auth.tenant === 'alliance-retail-group' && !this.isAMAP) {
          const activeAMAPs = validAllowances.filter(a => a.billing_mode === 'AMAP')
          if (activeAMAPs.length > 0) {
            promoItem.item.amap_allowances = activeAMAPs
            const totalAMAPs = this.sumOverlappingAllowances(activeAMAPs, 'amap_allowance')
            promoItem.amap_allowance_total = `$${this.$config.formatCurrency(totalAMAPs)}`
          }
        }
      }
      const netUnitCost = this.calcItemNetUnitCost(promoItem)
      const grossProfit = this.calcGrossProfit(promoItem, netUnitCost)
      promoItem.net_unit_cost = netUnitCost
      promoItem.gross_profit = grossProfit

      if (this.$auth.tenant === 'alliance-retail-group' && !this.isComplexPromo) {
        const netUnitAllowance = this.getNetUnitAllowance(promoItem)
        promoItem.net_unit_allowance = netUnitAllowance
      }
      if (this.tenant === 'alliance-retail-group' && promoItem.contract_case_cost) {
        const contractNetUnitCost = this.calcItemNetUnitCost(promoItem, true)
        const contractGrossProfit = this.calcGrossProfit(promoItem, contractNetUnitCost)
        promoItem.contract_net_unit_cost = contractNetUnitCost
        promoItem.contract_gross_profit = contractGrossProfit
      }
      
      if (this.$auth.tenant === 'alliance-retail-group' && this.isComplexPromo) {
        promoItem.complex_net_unit_cost = this.calcComplexNetUnitCost(promoItem)
      }
      return promoItem
    },
    getNetUnitAllowance(promoItem) {
      const validNetUnitAllowances = this.getValidActiveAllowances(promoItem, this.netUnitAllowanceCostStackConfig)
        promoItem.has_net_unit_active_allowances = (validNetUnitAllowances.length > 0)
        if (promoItem.has_net_unit_active_allowances) {
          promoItem.item.filtered_net_unit_allowances = validNetUnitAllowances
        }
        return this.calcItemNetUnitAllowance(promoItem)
    },
    calcItemNetUnitCost (promoItem, contract = false) {
      let sumScans = 0
      let sumBillbacks = 0
      if (!(this.$auth.tenant === 'alliance-retail-group' && this.isComplexPromo)) {
        sumScans = this.calcSumAllowances(promoItem, this.scanAllowanceFields)
        sumBillbacks = this.calcSumAllowances(promoItem, this.billbackAllowanceFields)
      }
      if (this.$auth.tenant === 'pricechopper' && this.isComplexPromo) {
        const groupQty = promoItem.promo_item_group.qty
        const complexSumScans = (sumScans / groupQty)
        sumScans = complexSumScans
      }
      if (promoItem?.has_active_allowances) {
        const validAllowances = (this.$auth.tenant === 'alliance-retail-group') 
          ? promoItem.item.filtered_allowances.filter(a => a.cost_stack_include)
          : promoItem.item.filtered_allowances

        const allowanceTotals = this.calcSumActiveAllowances(validAllowances)
        const sumScansActive = allowanceTotals.sumScans
        const sumBillbacksActive = allowanceTotals.sumBillbacks
        const sumEBAs = allowanceTotals.sumEBAs
        const sumAMAPs = (contract && promoItem.contract_amap) ? promoItem.contract_amap : allowanceTotals.sumAMAPs
        sumScans = (sumScans + sumScansActive)
        sumBillbacks = (sumBillbacks + sumBillbacksActive + sumAMAPs + sumEBAs)
      } else if (contract && promoItem.contract_amap) {
        sumBillbacks = (sumBillbacks + promoItem.contract_amap)
      }
      const qty_per_case = this.usePromoItemQtyPerCase 
        ? promoItem.promo_item_qty_per_case || promoItem.item.qty_per_case
        : promoItem.item.qty_per_case
      
      const cost = (contract && promoItem.contract_case_cost) ? promoItem.contract_case_cost  : promoItem.cost
      return this.calcNetUnitCost(cost, qty_per_case, sumScans, sumBillbacks)
    },
    calcComplexNetUnitCost (promoItem) {
      if (
        (this.promo.promotion_type.constant === 'BUY_X_SAVE_$Y' && this.promo.promo_category?.name === 'When You Buy') ||
        (this.promo.promotion_type.constant === 'BUY_X_GET_X' && !promoItem.promo_item_group.buy_item && promoItem.promo_item_group.get_item)
      ) {
        let sumScans = this.calcSumAllowances(promoItem, this.scanAllowanceFields, true)
        let sumBillbacks = this.calcSumAllowances(promoItem, this.billbackAllowanceFields)
        const groupQty = promoItem.promo_item_group.qty
        const complexSumScans = (sumScans / groupQty)
        sumScans = complexSumScans
        if (promoItem?.has_active_allowances) {
          const validAllowances = promoItem.item.filtered_allowances.filter(a => a.cost_stack_include)
          const allowanceTotals = this.calcSumActiveAllowances(validAllowances)
          const sumScansActive = allowanceTotals.sumScans
          const sumBillbacksActive = allowanceTotals.sumBillbacks
          const sumEBAs = allowanceTotals.sumEBAs
          const sumAMAPs = allowanceTotals.sumAMAPs
          sumScans = (sumScans + sumScansActive)
          sumBillbacks = (sumBillbacks + sumBillbacksActive + sumAMAPs + sumEBAs)
        }
        const qty_per_case = this.usePromoItemQtyPerCase 
          ? promoItem.promo_item_qty_per_case || promoItem.item.qty_per_case
          : promoItem.item.qty_per_case
        return this.calcNetUnitCost(promoItem.cost, qty_per_case, sumScans, sumBillbacks)
      } else {
        return promoItem.net_unit_cost ? promoItem.net_unit_cost : this.calcItemNetUnitCost(promoItem)
      }
    },
    calcItemNetUnitAllowance (promoItem) {
      let sumScans = this.calcSumAllowances(promoItem, this.scanAllowanceFields)
      let sumBillbacks = this.calcSumAllowances(promoItem, this.billbackAllowanceFields)

      if (promoItem?.has_net_unit_active_allowances) {
        const validAllowances = promoItem.item.filtered_net_unit_allowances.filter(a => a.cost_stack_include)
        const allowanceTotals = this.calcSumActiveAllowances(validAllowances)
        const sumScansActive = allowanceTotals.sumScans
        const sumBillbacksActive = allowanceTotals.sumBillbacks
        const sumEBAs = allowanceTotals.sumEBAs
        const sumAMAPs = allowanceTotals.sumAMAPs
        sumScans = (sumScans + sumScansActive)
        sumBillbacks = (sumBillbacks + sumBillbacksActive + sumAMAPs + sumEBAs)
      }
      const qty_per_case = this.usePromoItemQtyPerCase 
        ? promoItem.promo_item_qty_per_case || promoItem.item.qty_per_case
        : promoItem.item.qty_per_case
      promoItem.sum_scan_allowances = sumScans
      promoItem.sum_billback_allowances = sumBillbacks
      return this.calcNetUnitAllowance(qty_per_case, sumScans, sumBillbacks)
    },
    calcSumActiveAllowances (activeAllowances) {
      const sumTPRScans = this.sumMergedAllowances(activeAllowances, 'TPR', 'tpr_scan')
      const sumEDLCScans = this.sumMergedAllowances(activeAllowances, 'EDLC', 'edlc_scan')
      const sumOtherScans = activeAllowances.reduce((total, allowance) => {
        const value = this.calcSumAllowances(allowance, ['rebate_amount', 'ad_scan'])
        return total + value
      }, 0)
      const sumScans = (sumTPRScans + sumEDLCScans + sumOtherScans)

      const activeAMAPs = activeAllowances.filter(a => a.billing_mode === 'AMAP')
      const activeEBAs = activeAllowances.filter(a => a.billing_mode === 'EBA')
      const sumAMAPs =  this.sumOverlappingAllowances(activeAMAPs, 'amap_allowance')
      const sumEBAs = this.sumOverlappingAllowances(activeEBAs, 'early_buy_allowance')

      const sumTPRBillbacks = this.sumMergedAllowances(activeAllowances, 'TPR', 'tpr_billback')
      const sumEDLCBillbacks = this.sumMergedAllowances(activeAllowances, 'EDLC', 'edlc_billback')
      const sumOtherBillbacks = activeAllowances.reduce((total, allowance) => {
        const value = this.calcSumAllowances(allowance, ['ad_billback', 'billback_allowance'])
        return total + value
      }, 0)
      const sumBillbacks = (sumTPRBillbacks + sumEDLCBillbacks + sumOtherBillbacks)

      return { sumScans, sumBillbacks, sumAMAPs, sumEBAs }
    },
    calcSumAllowances (allowanceItem, allowanceFields, overrideRedemptionAllowance = false) {
      return allowanceFields.reduce((total, field) => {
        const value = field === 'rebate_amount' && overrideRedemptionAllowance ? this.maxBuyItemGroupRedemptionAllowance : allowanceItem[field]
        return total + (value ? this.getNumericPrice(value) : 0)
      }, 0)
    },
    sumOverlappingAllowances(activeAllowances, allowType) {
      let total = 0
      if (activeAllowances.length == 0) return total

      let sums = []
      const ranges = this.getDateRanges(activeAllowances)

      for (const range of ranges) {
        const rangeStart = new Date(range.start_dt).setHours(0, 0, 0, 0)
        const rangeEnd = new Date(range.end_dt).setHours(0, 0, 0, 0)

        const overlappingPromos = activeAllowances.filter(p => {
          const promoStart = new Date(p.start_dt).setHours(0, 0, 0, 0)
          const promoEnd = new Date(p.end_dt).setHours(0, 0, 0, 0)
          return ((rangeStart < promoEnd) && (promoStart < rangeEnd))
        })
        if (overlappingPromos.length > 0) {
          const sum = overlappingPromos.reduce((total, promo) => {
            return total + this.getNumericPrice(promo[allowType])
          }, 0)
          sums.push(sum)
        }
      }
      if (sums.length > 0) {
        const max = Math.max(...sums)
        total += max
      }
      return total
    },
    sumMergedAllowances(activeAllowances, billing_mode, field) {
      const filtered_allowances = activeAllowances.filter(a => a.billing_mode === billing_mode && (this.getNumericPrice(a[field]) > 0))
      const merged = this.getMergedActiveAllowances(filtered_allowances, field)
      let sum = 0
      if (merged.length > 0) {
        sum = merged.reduce((total, allowance) => {
          const value = this.calcSumAllowances(allowance, [field])
          return total + value
        }, 0)
      }
      return sum
    },
    getMergedActiveAllowances(activeAllowances, allowType) {
      if (activeAllowances.length == 0) return []

      const msPerDay = 1000 * 60 * 60 * 24

      // Sort the promos by start date
      const promos = [...activeAllowances].map(p => ({
        ...p,
        startMs: new Date(p.start_dt).setHours(0, 0, 0, 0),
        endMs: new Date(p.end_dt).setHours(0, 0, 0, 0),
        allowValue: this.getNumericPrice(p[allowType])
      })).sort((a, b) => a.startMs - b.startMs)

      // Merge promos that have the same allowance and occur back to back
      const mergedPromos = []
      for (const p of promos) {
        if (mergedPromos.length === 0) {
          mergedPromos.push({ ...p })
          continue
        }
        const last = mergedPromos[mergedPromos.length - 1]
        
        const diffDays = Math.round((p.startMs - last.endMs) / msPerDay)
        const isBackToBack = diffDays === 1
        const isSameAllowance = last.allowValue === p.allowValue

        if (isBackToBack && isSameAllowance) {
          last.end_dt = p.end_dt
          last.endMs = Math.max(last.endMs, p.endMs)
        } else {
          mergedPromos.push({ ...p })
        }
      }

      return mergedPromos
    },
    getDateRanges(promos) {
      // flatten sets of promo start/end dates into single array
      const dates = promos.flatMap(p => ([p.start_dt, p.end_dt]))
      const sortedDates = [...new Set(dates)].toSorted((a, b) => {
        return new Date(a).setHours(0, 0, 0, 0) - new Date(b).setHours(0, 0, 0, 0)
      })
      const ranges = []
      for (const [i, date] of sortedDates.entries()) {
        const remainingDates = sortedDates.slice(i + 1)
        if (remainingDates?.length > 0) {
          const nextDate = remainingDates.find(d => (d > date))
          if (nextDate) {
            ranges.push({ start_dt: date, end_dt: nextDate })
          }
        }
      }
      return ranges
    },
    getValidActiveAllowances (promoItem, costStackConfig) {
      if (promoItem?.item?.active_allowances) {
        const { active_allowances } = promoItem.item
        const filtered_allowances = active_allowances.filter(a => {
          if (costStackConfig[this.promo.billing_mode.constant].includes(a.billing_mode)) {
            if ((a.promo_id !== this.promo.id) && !['CANCELLED', 'REJECTED', 'INCLUDED_IN_MERGE'].includes(a.status_constant)) {
              if (['BUY_X_GET_X', 'BUY_X_SAVE_$Y'].includes(a.promo_type)) {
                return this.isComplexPromo && (this.$auth.tenant !== 'alliance-retail-group')
              }
              return ((this.$auth.tenant !== 'alliance-retail-group') || a.promo_party_ids?.some(id => this.promoPartyIds.includes(id)))
            }
          }
          return false
        })
        return filtered_allowances.map(a => {
          return {
            ...a,
            cost_stack_include: (['AMAP', 'EBA'].includes(a.billing_mode) || this.promoPartyIds.every(id => a.promo_party_ids.includes(id)))
          }
        })
      }
      return []
    },
    calcTotalAMAPs (amaps) {
      const allowances = amaps.map(amap => amap.amap_allowance)
      return allowances.reduce((total, value) => {
        return total + value
      }, 0)
    },
    calcNetUnitCost (cost, qty_per_case, sumScans, sumBillbacks) {
      let netUnitCost = ''

      if (cost != null && qty_per_case != null) {
        // remove formatting/normalize
        cost = this.getNumericPrice(cost)
        qty_per_case = Number(qty_per_case)

        if (cost > 0 && qty_per_case > 0 && (sumScans + sumBillbacks) >= 0) {
          netUnitCost = ((cost - sumBillbacks) / qty_per_case) - sumScans
          // if calculated value isn't a positive number, set to 0
          if (Math.sign(netUnitCost) !== 1) netUnitCost = 0
        }
        if (netUnitCost !== '' && !Number.isNaN(netUnitCost)) {
          netUnitCost = `$${this.$config.formatCurrency(netUnitCost)}`
        }
      }
      return netUnitCost
    },
    calcGrossProfit (promoItem, netUnitCost) {
      let grossProfit = ''
      netUnitCost = this.getNumericPrice(netUnitCost)
      const promoPrice = this.getNumericPrice(promoItem.promotion_price)

      if (promoPrice > 0 && netUnitCost > 0) {
        const amount = (promoPrice - netUnitCost) / promoPrice
        grossProfit = `${(amount * 100).toFixed(2)}%`
      }
      return grossProfit
    },
    calcNetUnitAllowance (qty_per_case, sumScans, sumBillbacks) {
      let netUnitAllowance = ''
      if (qty_per_case != null) {
        qty_per_case = Number(qty_per_case)
        if (qty_per_case > 0 && (sumScans + sumBillbacks) >= 0) {
          netUnitAllowance = (sumBillbacks / qty_per_case) + sumScans
          if (Math.sign(netUnitAllowance) !== 1) netUnitAllowance = 0
        }
      }
      return netUnitAllowance
    },
    getFormattedDisplayValue (promoItem, key) {
      const group = promoItem.promo_item_group
      let value = promoItem[key] || group[key]
      if (value == null && this.promo[key]) {
        value = this.promo[key]
      }
      if (key === 'retail_price' && promoItem.item.sale_price !== null) {
        if ((this.$auth.tenant === 'alliance-retail-group') || value == null) {
          value = promoItem.item.sale_price
        }
      }
      if (value == null && key === 'cost' && promoItem.item.cost !== null) {
        value = promoItem.item.cost
      }
      return this.getFormattedPrice(value)
    },
  }
}
